import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate, useSearchParams, useParams } from 'react-router-dom';
import axios from 'axios';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { PremiumBadge } from '../../components/ui/premium-badge';
import { PremiumUpgradeDialog } from '../../components/ui/premium-overlay';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { Spinner } from '../../components/ui/spinner';
import { useToast } from '../../hooks/use-toast';
import { formatCurrency, formatDate } from '../../lib/i18n';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import {
  Plus,
  FileText,
  Download,
  CheckCircle,
  Clock,
  AlertCircle,
  AlertTriangle,
  X,
  Search,
  Calendar
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusColors = {
  paid: 'bg-green-100 text-green-800',
  partial: 'bg-yellow-100 text-yellow-800',
  unpaid: 'bg-red-100 text-red-800'
};

const statusLabels = {
  en: { paid: 'Paid', partial: 'Partial', unpaid: 'Unpaid' },
  bn: { paid: 'পরিশোধিত', partial: 'আংশিক', unpaid: 'অপরিশোধিত' }
};

export default function InvoicesListPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const jobId = searchParams.get('job_id');
  const { t, language } = useLanguage();
  const { garageSlug } = useParams();
  const basePath = garageSlug ? `/${garageSlug}/admin` : '/admin';
  const { toast } = useToast();
  const { isPremium } = useAuth();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const dateFromRef = useRef(null);
  const dateToRef = useRef(null);
  const paidInputRef = useRef(null);
  const [totalCount, setTotalCount] = useState(0);
  const [todayCount, setTodayCount] = useState(0);
  const isSearching = !!(search || dateFrom || dateTo || statusFilter !== 'all');

  // New Invoice Form State
  const [showCreateForm, setShowCreateForm] = useState(!!jobId);
  const [job, setJob] = useState(null);
  const [existingInvoice, setExistingInvoice] = useState(null);
  const [invoiceForm, setInvoiceForm] = useState({
    job_id: jobId || '',
    discount_percent: 0,
    discount_amount: 0,
    redeem_points: 0,
    payment_method: 'cash',
    paid_amount: '',
    notes: ''
  });
  const [loyaltySettings, setLoyaltySettings] = useState(null);
  const [customerPoints, setCustomerPoints] = useState(null);
  const [splitPayment, setSplitPayment] = useState(false);
  const [splitMethod, setSplitMethod] = useState('bkash');
  const [splitAmount, setSplitAmount] = useState('');

  // Due confirmation dialog
  const [showDueConfirm, setShowDueConfirm] = useState(false);
  const [upgradePopup, setUpgradePopup] = useState({ open: false, feature: '' });

  useEffect(() => {
    fetchInvoices();
    if (jobId) {
      fetchJob(jobId);
    }
  }, [jobId]);

  // Auto-focus paid input when job is loaded
  useEffect(() => {
    if (job && paidInputRef.current) {
      setTimeout(() => paidInputRef.current?.focus(), 100);
    }
  }, [job]);

  // Debounced auto-search on typing
  useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(true);
      fetchInvoices();
    }, 350);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    setLoading(true);
    fetchInvoices();
  }, [statusFilter, dateFrom, dateTo]);

  const fetchInvoices = async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);
      
      const [response, countRes] = await Promise.all([
        axios.get(`${API}/invoices?${params}`),
        axios.get(`${API}/invoices/count?${params}`)
      ]);
      setInvoices(response.data);
      setTotalCount(countRes.data.count);
      setTodayCount(countRes.data.today_count);
    } catch (error) {
      console.error('Failed to fetch invoices:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchJob = async (id) => {
    try {
      // First check if invoice already exists for this job
      const checkRes = await axios.get(`${API}/invoices/check-job/${id}`);
      if (checkRes.data.exists) {
        setExistingInvoice(checkRes.data);
        // If invoice exists, redirect to it
        navigate(`${basePath}/invoices/${checkRes.data.invoice_id}`);
        return;
      }
      const response = await axios.get(`${API}/jobs/${id}`);
      setJob(response.data);
      setInvoiceForm(prev => ({
        ...prev,
        job_id: id,
        redeem_points: 0,
        paid_amount: ''
      }));
    } catch (error) {
      console.error('Failed to fetch job:', error);
    }
  };

  const fetchLoyaltyForJob = async (jobData) => {
    if (!jobData?.customer_id) {
      setLoyaltySettings(null);
      setCustomerPoints(null);
      return;
    }
    try {
      const [settingsRes, pointsRes] = await Promise.all([
        axios.get(`${API}/marketing/loyalty/settings`),
        axios.get(`${API}/marketing/loyalty/customers/${jobData.customer_id}/points`)
      ]);
      setLoyaltySettings(settingsRes.data || null);
      setCustomerPoints(pointsRes.data || null);
    } catch (error) {
      setLoyaltySettings(null);
      setCustomerPoints(null);
    }
  };

  useEffect(() => {
    if (job) {
      fetchLoyaltyForJob(job);
    }
  }, [job]);

  const handleCreateInvoice = () => {
    if (!invoiceForm.job_id) {
      toast({ title: language === 'en' ? 'Please select a job' : 'একটি জব নির্বাচন করুন', variant: 'destructive' });
      return;
    }

    const paidAmt = parseInt(invoiceForm.paid_amount) || 0;
    const splitAmt = splitPayment ? (parseInt(splitAmount) || 0) : 0;
    const due = Math.max(0, totals.total - paidAmt - splitAmt);

    if (due > 0) {
      setShowDueConfirm(true);
      return;
    }

    createInvoice();
  };

  const createInvoice = async () => {
    setCreating(true);
    setShowDueConfirm(false);
    try {
      const splitAmt = splitPayment ? (parseInt(splitAmount) || 0) : 0;
      const payload = {
        ...invoiceForm,
        paid_amount: parseInt(invoiceForm.paid_amount) || 0,
        discount_percent: parseInt(invoiceForm.discount_percent) || 0,
        discount_amount: parseInt(invoiceForm.discount_amount) || 0,
        redeem_points: parseInt(invoiceForm.redeem_points) || 0,
        split_payment_method: splitPayment ? splitMethod : null,
        split_payment_amount: splitAmt,
      };
      const response = await axios.post(`${API}/invoices`, payload);
      toast({ title: language === 'en' ? 'Invoice created!' : '\u099A\u09BE\u09B2\u09BE\u09A8 \u09A4\u09C8\u09B0\u09BF \u09B9\u09AF\u09BC\u09C7\u099B\u09C7!' });
      navigate(`${basePath}/invoices/${response.data.id}`);
    } catch (error) {
      // Handle 409 - invoice already exists for this job
      if (error.response?.status === 409) {
        const detail = error.response.data?.detail || '';
        const existingId = detail.split(': ')[1];
        if (existingId) {
          toast({ title: language === 'en' ? 'Invoice already exists for this job. Redirecting...' : '\u098F\u0987 \u099C\u09AC\u09C7\u09B0 \u099A\u09BE\u09B2\u09BE\u09A8 \u0986\u099B\u09C7\u0964 \u09B0\u09BF\u09A1\u09BE\u0987\u09B0\u09C7\u0995\u09CD\u099F \u09B9\u099A\u09CD\u099B\u09C7...' });
          navigate(`${basePath}/invoices/${existingId}`);
        } else {
          toast({ title: language === 'en' ? 'Invoice already exists for this job' : '\u098F\u0987 \u099C\u09AC\u09C7\u09B0 \u099A\u09BE\u09B2\u09BE\u09A8 \u0986\u099B\u09C7', variant: 'destructive' });
        }
      } else {
        toast({ title: language === 'en' ? 'Failed to create invoice' : '\u099A\u09BE\u09B2\u09BE\u09A8 \u09A4\u09C8\u09B0\u09BF \u09AC\u09CD\u09AF\u09B0\u09CD\u09A5', variant: 'destructive' });
      }
    } finally {
      setCreating(false);
    }
  };

  const downloadPdf = async (invoiceId) => {
    try {
      const response = await axios.get(`${API}/invoices/${invoiceId}/pdf`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `invoice_${invoiceId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast({ title: 'Failed to download PDF', variant: 'destructive' });
    }
  };

  const paymentMethods = [
    { value: 'cash', label: language === 'en' ? 'Cash' : 'নগদ' },
    { value: 'bkash', label: 'bKash' },
    { value: 'nagad', label: 'Nagad' },
    { value: 'bank', label: language === 'en' ? 'Bank Transfer' : 'ব্যাংক ট্রান্সফার' },
    { value: 'card', label: language === 'en' ? 'Card' : 'কার্ড' },
  ];

  // Helper: update form and reset paid amount
  const updateFormField = (updater) => {
    setInvoiceForm(prev => ({
      ...updater(prev),
      paid_amount: ''
    }));
  };

  const calculateTotal = () => {
    if (!job) return { subtotal: 0, discount: 0, total: 0 };
    const subtotal = job.total_amount;
    const discountAmt = parseInt(invoiceForm.discount_amount) || 0;
    const discountPct = parseInt(invoiceForm.discount_percent) || 0;
    const discount = discountAmt || Math.floor(subtotal * discountPct / 100);
    let pointsDiscount = 0;
    if (loyaltySettings?.is_enabled && (parseInt(invoiceForm.redeem_points) || 0) > 0) {
      const redeemRatePoints = loyaltySettings.redeem_rate_points || 0;
      const redeemRateDiscount = loyaltySettings.redeem_rate_discount || 0;
      if (redeemRatePoints > 0) {
        pointsDiscount = ((parseInt(invoiceForm.redeem_points) || 0) / redeemRatePoints) * redeemRateDiscount;
      }
    }
    const total = Math.max(0, subtotal - discount - pointsDiscount);
    return { subtotal, discount, pointsDiscount, total };
  };

  const totals = calculateTotal();
  const paidAmount = parseInt(invoiceForm.paid_amount) || 0;
  const splitAmountVal = splitPayment ? (parseInt(splitAmount) || 0) : 0;
  const totalPaid = paidAmount + splitAmountVal;
  const dueAmount = Math.max(0, totals.total - totalPaid);

  // Focus handler: clear 0 on focus
  const handleIntFocus = (e) => {
    if (e.target.value === '0') e.target.value = '';
  };

  // Validate discount doesn't exceed total
  const validateDiscountPercent = (val) => {
    const v = parseInt(val) || 0;
    return Math.max(0, Math.min(100, v));
  };
  const validateDiscountAmount = (val) => {
    const v = parseInt(val) || 0;
    if (!job) return Math.max(0, v);
    return Math.max(0, Math.min(job.total_amount, v));
  };
  const validatePaidAmount = (val) => {
    const v = parseInt(val) || 0;
    const splitAmt = splitPayment ? (parseInt(splitAmount) || 0) : 0;
    return Math.max(0, Math.min(totals.total - splitAmt, v));
  };
  const validateSplitAmount = (val) => {
    const v = parseInt(val) || 0;
    const mainAmt = parseInt(invoiceForm.paid_amount) || 0;
    return Math.max(0, Math.min(totals.total - mainAmt, v));
  };
  const validateRedeemPoints = (val) => {
    const v = parseInt(val) || 0;
    const available = customerPoints?.current_balance || 0;
    return Math.max(0, Math.min(available, v));
  };

  // Is Create Invoice button enabled?
  const isCreateDisabled = creating || invoiceForm.paid_amount === '' || paidAmount < 0;

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold font-['Barlow_Condensed']">{t('invoices_title')}</h1>
            <p className="text-slate-500">
              {isSearching
                ? (language === 'en' ? `${invoices.length} results found` : `${invoices.length}\u099F\u09BF \u09AB\u09B2\u09BE\u09AB\u09B2 \u09AA\u09BE\u0993\u09AF\u09BC\u09BE \u0997\u09C7\u099B\u09C7`)
                : (language === 'en' ? `${totalCount} invoices` : `${totalCount}\u099F\u09BF \u099A\u09BE\u09B2\u09BE\u09A8`)}
              {' '}
              <span className="text-xs text-blue-600 font-medium">
                ({language === 'en' ? `${todayCount} today` : `\u0986\u099C ${todayCount}\u099F\u09BF`})
              </span>
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => navigate(`${basePath}/invoices/dues`)}
              className="gap-2 text-orange-700 border-orange-300 hover:bg-orange-50"
            >
              <AlertCircle className="w-4 h-4" />
              {language === 'en' ? 'View Dues' : 'বকেয়া দেখুন'}
            </Button>
            {!showCreateForm && (
              <Button 
                onClick={() => setShowCreateForm(true)}
                className="gap-2 bg-blue-800 hover:bg-blue-900"
                data-testid="new-invoice-btn"
              >
                <Plus className="w-4 h-4" />
                {t('create_invoice')}
              </Button>
            )}
          </div>
        </div>

        {/* Create Invoice Form - centered, narrower card */}
        {showCreateForm && (
          <div className="max-w-2xl mx-auto">
            <Card className="border-2 border-blue-300 shadow-lg bg-gradient-to-b from-blue-50 to-white">
              <CardHeader className="pb-3 flex flex-row items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5 text-blue-700" />
                  {t('create_invoice')}
                </CardTitle>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 p-0 text-slate-400 hover:text-slate-600"
                  onClick={() => {
                    setShowCreateForm(false);
                    setJob(null);
                  }}
                >
                  <X className="w-4 h-4" />
                </Button>
              </CardHeader>
              <CardContent className="space-y-4">
                {job ? (
                  <>
                    {/* Job summary */}
                    <div className="p-4 bg-white rounded-lg border border-slate-200">
                      <div className="flex justify-between items-start">
                        <div>
                          <Link to={`${basePath}/jobs/${job.id}`} className="font-semibold text-blue-800 hover:underline">
                            {job.job_number}
                          </Link>
                          <p className="text-sm text-slate-600">{job.customer_name} &bull; {job.customer_phone}</p>
                          <p className="text-sm text-slate-500">{job.bike_make} {job.bike_model}</p>
                        </div>
                        <p className="text-2xl font-bold">{formatCurrency(job.total_amount, language)}</p>
                      </div>
                    </div>

                    {/* Loyalty redemption */}
                    {loyaltySettings?.is_enabled && (customerPoints?.current_balance || 0) > 0 && (
                      <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                        <div className="flex items-center justify-between mb-3">
                          <p className="text-sm font-semibold text-yellow-900">
                            {language === 'en' ? 'Redeem Loyalty Points' : 'লয়্যালটি পয়েন্ট রিডিম'}
                          </p>
                          <Badge className="bg-yellow-100 text-yellow-800 border-yellow-300">
                            {customerPoints.current_balance || 0} pts
                          </Badge>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <div>
                            <Label className="text-xs text-slate-600">{language === 'en' ? 'Points to redeem' : 'রিডিম পয়েন্ট'}</Label>
                            <Input
                              type="number"
                              min="0"
                              max={customerPoints.current_balance || 0}
                              step="1"
                              value={invoiceForm.redeem_points}
                              onFocus={() => {
                                if ((parseInt(invoiceForm.redeem_points) || 0) === 0) {
                                  setInvoiceForm(prev => ({ ...prev, redeem_points: '' }));
                                }
                              }}
                              onChange={(e) => {
                                const raw = e.target.value;
                                if (raw === '') {
                                  setInvoiceForm(prev => ({ ...prev, redeem_points: '', paid_amount: 0 }));
                                  return;
                                }
                                const val = validateRedeemPoints(raw);
                                setInvoiceForm(prev => ({ ...prev, redeem_points: val, paid_amount: 0 }));
                              }}
                            />
                          </div>
                          <div className="text-xs text-slate-600 flex items-end pb-2">
                            <div>
                              <p>
                                {language === 'en' ? 'Minimum' : 'সর্বনিম্ন'}: {loyaltySettings.min_redeem_points || 0} {language === 'en' ? 'pts' : 'পয়েন্ট'}
                              </p>
                              <p>
                                {loyaltySettings.redeem_rate_points || 0} {language === 'en' ? 'pts' : 'পয়েন্ট'} = ৳{loyaltySettings.redeem_rate_discount || 0}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Discount fields */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-xs text-slate-500">{language === 'en' ? 'Discount %' : 'ছাড় %'}</Label>
                        <Input
                          type="number"
                          min="0"
                          max="100"
                          step="1"
                          value={invoiceForm.discount_percent}
                          onFocus={handleIntFocus}
                          onChange={(e) => {
                            const val = validateDiscountPercent(e.target.value);
                            updateFormField(prev => ({
                              ...prev,
                              discount_percent: val,
                              discount_amount: 0,
                            }));
                          }}
                          data-testid="discount-percent"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-slate-500">{language === 'en' ? 'Or Discount Amount' : 'অথবা ছাড়ের পরিমাণ'}</Label>
                        <Input
                          type="number"
                          min="0"
                          step="1"
                          value={invoiceForm.discount_amount}
                          onFocus={handleIntFocus}
                          onChange={(e) => {
                            const val = validateDiscountAmount(e.target.value);
                            updateFormField(prev => ({
                              ...prev,
                              discount_amount: val,
                              discount_percent: 0,
                            }));
                          }}
                          data-testid="discount-amount"
                        />
                      </div>
                    </div>

                    {/* Payment method & paid amount */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-xs text-slate-500">{t('payment_method')}</Label>
                        <Select
                          value={invoiceForm.payment_method}
                          onValueChange={(v) => updateFormField(prev => ({ ...prev, payment_method: v }))}
                        >
                          <SelectTrigger data-testid="payment-method">
                            <SelectValue placeholder={language === 'en' ? 'Select method...' : 'পদ্ধতি নির্বাচন করুন...'} />
                          </SelectTrigger>
                          <SelectContent>
                            {paymentMethods.map((m) => (
                              <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-xs text-slate-500">{t('paid_amount')}</Label>
                        <Input
                          type="number"
                          min="0"
                          step="1"
                          placeholder={language === 'en' ? 'Enter amount' : 'পরিমাণ লিখুন'}
                          value={invoiceForm.paid_amount}
                          ref={paidInputRef}
                          onFocus={() => {
                            if ((parseInt(invoiceForm.paid_amount) || 0) === 0) {
                              setInvoiceForm(prev => ({ ...prev, paid_amount: '' }));
                            }
                          }}
                          onChange={(e) => {
                            const raw = e.target.value;
                            if (raw === '') {
                              setInvoiceForm(prev => ({ ...prev, paid_amount: '' }));
                            } else {
                              const val = validatePaidAmount(raw);
                              setInvoiceForm(prev => ({ ...prev, paid_amount: val }));
                            }
                          }}
                          data-testid="paid-amount"
                        />
                      </div>
                    </div>

                    {/* Split Payment Toggle */}
                    <div className="flex items-center justify-between py-1">
                      <Label className="text-xs text-slate-500 flex items-center gap-1">{language === 'en' ? 'Split Payment (2 methods)' : 'স্প্লিট পেমেন্ট (২ পদ্ধতি)'}{!isPremium && <PremiumBadge size="sm" />}</Label>
                      <Switch checked={splitPayment} onCheckedChange={(v) => { if (!isPremium) { setUpgradePopup({ open: true, feature: language === 'en' ? 'Split Payment' : 'বিভক্ত পেমেন্ট' }); return; } setSplitPayment(v); if (!v) setSplitAmount(''); }} />
                    </div>

                    {/* Split Payment Fields */}
                    {splitPayment && (
                      <div className="grid grid-cols-2 gap-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                        <div>
                          <Label className="text-xs text-slate-500">{language === 'en' ? '2nd Method' : '২য় পদ্ধতি'}</Label>
                          <Select value={splitMethod} onValueChange={setSplitMethod}>
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {paymentMethods.filter(m => m.value !== invoiceForm.payment_method).map((m) => (
                                <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-xs text-slate-500">{language === 'en' ? '2nd Amount' : '২য় পরিমাণ'}</Label>
                          <Input
                            type="number"
                            min="0"
                            step="1"
                            placeholder={language === 'en' ? 'Amount' : 'পরিমাণ'}
                            value={splitAmount}
                            onFocus={() => { if ((parseInt(splitAmount) || 0) === 0) setSplitAmount(''); }}
                            onChange={(e) => {
                              const raw = e.target.value;
                              if (raw === '') { setSplitAmount(''); } else { setSplitAmount(validateSplitAmount(raw)); }
                            }}
                          />
                        </div>
                      </div>
                    )}

                    {/* Summary */}
                    <div className="p-4 bg-slate-900 text-white rounded-lg">
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-slate-400">{t('subtotal')}</span>
                          <span>{formatCurrency(totals.subtotal, language)}</span>
                        </div>
                        {totals.discount > 0 && (
                          <div className="flex justify-between">
                            <span className="text-slate-400">{t('discount')}</span>
                            <span className="text-red-400">-{formatCurrency(totals.discount, language)}</span>
                          </div>
                        )}
                        {totals.pointsDiscount > 0 && (
                          <div className="flex justify-between">
                            <span className="text-slate-400">{language === 'en' ? 'Points Discount' : 'পয়েন্ট ডিসকাউন্ট'}</span>
                            <span className="text-yellow-300">-{formatCurrency(totals.pointsDiscount, language)}</span>
                          </div>
                        )}
                        <div className="flex justify-between pt-2 border-t border-slate-700">
                          <span className="font-bold text-base">{t('total')}</span>
                          <span className="text-xl font-bold">{formatCurrency(totals.total, language)}</span>
                        </div>
                        {invoiceForm.paid_amount !== '' && (
                          <>
                            <div className="flex justify-between">
                              <span className="text-slate-400">{t('paid_amount')}</span>
                              <span className="text-green-400">{formatCurrency(totalPaid, language)}</span>
                            </div>
                            {splitPayment && splitAmountVal > 0 && (
                              <div className="flex justify-between text-xs">
                                <span className="text-slate-500">{paymentMethods.find(m => m.value === invoiceForm.payment_method)?.label}: {formatCurrency(paidAmount, language)} + {paymentMethods.find(m => m.value === splitMethod)?.label}: {formatCurrency(splitAmountVal, language)}</span>
                              </div>
                            )}
                            <div className="flex justify-between">
                              <span className="text-slate-400">{t('due_amount')}</span>
                              <span className={dueAmount > 0 ? 'text-red-400 font-semibold' : 'text-green-400'}>
                                {formatCurrency(dueAmount, language)}
                              </span>
                            </div>
                          </>
                        )}
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex gap-3">
                      <Button
                        onClick={handleCreateInvoice}
                        disabled={isCreateDisabled}
                        className="flex-1 bg-green-600 hover:bg-green-700 disabled:opacity-50"
                        data-testid="submit-invoice"
                      >
                        {creating ? <Spinner size="sm" /> : (language === 'en' ? 'Create Invoice' : 'চালান তৈরি করুন')}
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => {
                          setShowCreateForm(false);
                          setJob(null);
                        }}
                      >
                        {t('cancel')}
                      </Button>
                    </div>
                  </>
                ) : (
                  <div className="text-center py-8">
                    <p className="text-slate-500 mb-4">
                      {language === 'en' ? 'Select a completed job to create an invoice' : 'চালান তৈরি করতে একটি সম্পন্ন জব নির্বাচন করুন'}
                    </p>
                    <Link to={`${basePath}/jobs?status=completed`}>
                      <Button>{language === 'en' ? 'View Completed Jobs' : 'সম্পন্ন জব দেখুন'}</Button>
                    </Link>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Visual divider between create form and list */}
        {showCreateForm && invoices.length > 0 && (
          <div className="flex items-center gap-4 pt-2">
            <div className="h-px flex-1 bg-slate-300" />
            <span className="text-sm font-medium text-slate-400">
              {language === 'en' ? 'Previous Invoices' : 'পূর্ববর্তী চালান'}
            </span>
            <div className="h-px flex-1 bg-slate-300" />
          </div>
        )}

        {/* Search & Filters */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  placeholder={language === 'en' ? 'Search by invoice #, customer, phone, job #, vehicle...' : 'চালান নম্বর, গ্রাহক, ফোন, জব নম্বর, বাইক দিয়ে খুঁজুন...'}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10 pr-9 h-12"
                  data-testid="invoice-search"
                />
                {search && (
                  <button
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-red-400 hover:text-red-600 transition-colors"
                    onClick={() => setSearch('')}
                    aria-label="Clear search"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
              <div className="flex items-center gap-2 w-full sm:w-auto">
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-auto min-w-[120px] flex-[1.2] sm:flex-none sm:w-[180px] h-12" data-testid="invoice-status-filter">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{language === 'en' ? 'All Status' : 'সব স্ট্যাটাস'}</SelectItem>
                    <SelectItem value="paid">{statusLabels[language].paid}</SelectItem>
                    <SelectItem value="partial">{statusLabels[language].partial}</SelectItem>
                    <SelectItem value="unpaid">{statusLabels[language].unpaid}</SelectItem>
                    <SelectItem value="waived">{language === 'en' ? 'Waived' : 'মওকুফ'}</SelectItem>
                  </SelectContent>
                </Select>
                <div className="flex items-center gap-1 flex-1 min-w-0">
                  <Calendar className="w-4 h-4 text-slate-600 flex-shrink-0" />
                  <div
                    className="relative flex-1 min-w-0 h-10 border rounded-md flex items-center cursor-pointer hover:border-blue-400 transition-colors overflow-hidden"
                    onClick={() => dateFromRef.current?.showPicker?.()}
                  >
                    <input
                      ref={dateFromRef}
                      type="date"
                      value={dateFrom}
                      onChange={(e) => setDateFrom(e.target.value)}
                      className="sr-only"
                    />
                    <span className={`text-[11px] sm:text-sm px-1 sm:px-1.5 truncate ${dateFrom ? 'text-foreground' : 'text-slate-400'}`}>
                      {dateFrom ? new Date(dateFrom + 'T00:00:00').toLocaleDateString('en-GB') : 'dd/mm/yyyy'}
                    </span>
                  </div>
                  <span className="text-slate-300 text-xs flex-shrink-0">—</span>
                  <div
                    className="relative flex-1 min-w-0 h-10 border rounded-md flex items-center cursor-pointer hover:border-blue-400 transition-colors overflow-hidden"
                    onClick={() => dateToRef.current?.showPicker?.()}
                  >
                    <input
                      ref={dateToRef}
                      type="date"
                      value={dateTo}
                      onChange={(e) => setDateTo(e.target.value)}
                      className="sr-only"
                    />
                    <span className={`text-[11px] sm:text-sm px-1 sm:px-1.5 truncate ${dateTo ? 'text-foreground' : 'text-slate-400'}`}>
                      {dateTo ? new Date(dateTo + 'T00:00:00').toLocaleDateString('en-GB') : 'dd/mm/yyyy'}
                    </span>
                  </div>
                  {(dateFrom || dateTo) && (
                    <button
                      className="text-red-400 hover:text-red-600 transition-colors"
                      onClick={() => { setDateFrom(''); setDateTo(''); }}
                      aria-label="Clear date filter"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Invoices List */}
        {loading ? (
          <div className="flex justify-center py-20">
            <Spinner size="lg" />
          </div>
        ) : invoices.length > 0 ? (
          <div className="space-y-3">
            {(() => {
              const getDateGroup = (dateStr) => {
                const date = new Date(dateStr);
                const now = new Date();
                const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
                const yesterday = new Date(today); yesterday.setDate(today.getDate() - 1);
                const weekAgo = new Date(today); weekAgo.setDate(today.getDate() - 7);
                const monthAgo = new Date(today); monthAgo.setMonth(today.getMonth() - 1);
                
                if (date >= today) return language === 'en' ? 'Today' : 'আজ';
                if (date >= yesterday) return language === 'en' ? 'Yesterday' : 'গতকাল';
                if (date >= weekAgo) return language === 'en' ? 'This Week' : 'এই সপ্তাহ';
                if (date >= monthAgo) return language === 'en' ? 'This Month' : 'এই মাস';
                return language === 'en' ? 'Older' : 'পুরনো';
              };

              let lastGroup = null;
              return invoices.map((invoice) => {
                const group = getDateGroup(invoice.created_at);
                const showHeader = group !== lastGroup;
                lastGroup = group;

                return (
                  <React.Fragment key={invoice.id}>
                    {showHeader && (
                      <div className="flex items-center gap-3 pt-4 first:pt-0">
                        <div className="h-px flex-1 bg-slate-200" />
                        <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">{group}</span>
                        <div className="h-px flex-1 bg-slate-200" />
                      </div>
                    )}
                    <Card className="hover:border-blue-500 transition-colors" data-testid={`invoice-card-${invoice.id}`}>
                      <CardContent className="p-5">
                        <div className="flex items-start justify-between gap-4">
                          <Link to={`${basePath}/invoices/${invoice.id}`} className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <span className="font-bold text-blue-800 text-lg">{invoice.invoice_number}</span>
                              <Badge className={statusColors[invoice.status]}>
                                {statusLabels[language][invoice.status]}
                              </Badge>
                              {invoice.waived_amount > 0 && (
                                <Badge className="bg-orange-100 text-orange-800 border-orange-300 border text-xs">
                                  {language === 'en' ? 'Waived' : 'মওকুফ'}
                                </Badge>
                              )}
                            </div>
                            <p className="text-slate-600">
                              {invoice.customer_name} {invoice.customer_phone ? `\u2022 ${invoice.customer_phone}` : ''}
                            </p>
                            <p className="text-sm text-slate-500">
                              {invoice.bike_info} &bull; {formatDate(invoice.created_at, language)}
                            </p>
                            {invoice.job_number && (
                              <p className="text-xs text-slate-400 mt-1">
                                {language === 'en' ? 'Job' : '\u099C\u09AC'}: {invoice.job_number}
                              </p>
                            )}
                          </Link>

                          <div className="text-right">
                            <p className="text-2xl font-bold text-slate-900">
                              {formatCurrency(invoice.total, language)}
                            </p>
                            {invoice.due_amount > 0 && (
                              <p className="text-sm text-red-600">
                                {language === 'en' ? 'Due:' : '\u09AC\u0995\u09C7\u09AF\u09BC\u09BE:'} {formatCurrency(invoice.due_amount, language)}
                              </p>
                            )}
                            <div className="flex gap-2 mt-3">
                              <Link to={`${basePath}/invoices/${invoice.id}`}>
                                <Button variant="outline" size="sm">
                                  {t('view')}
                                </Button>
                              </Link>
                              <Button 
                                variant="outline" 
                                size="sm"
                                onClick={() => downloadPdf(invoice.id)}
                                data-testid={`download-pdf-${invoice.id}`}
                              >
                                <Download className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </React.Fragment>
                );
              });
            })()}
          </div>
        ) : (
          <Card>
            <CardContent className="py-20 text-center">
              <FileText className="w-16 h-16 mx-auto mb-4 text-slate-300" />
              <p className="text-slate-500 text-lg">
                {language === 'en' ? 'No invoices yet' : '\u098F\u0996\u09A8\u09CB \u0995\u09CB\u09A8 \u099A\u09BE\u09B2\u09BE\u09A8 \u09A8\u09C7\u0987'}
              </p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Due Amount Confirmation Dialog */}
      <AlertDialog open={showDueConfirm} onOpenChange={setShowDueConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-orange-500" />
              {language === 'en' ? 'Outstanding Due Amount' : '\u09AC\u0995\u09C7\u09AF\u09BC\u09BE \u09AA\u09B0\u09BF\u09AE\u09BE\u09A3 \u0986\u099B\u09C7'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {language === 'en'
                ? `This invoice will have a due amount of ${formatCurrency(dueAmount, language)}. Are you sure you want to create this invoice with an outstanding balance?`
                : `\u098F\u0987 \u099A\u09BE\u09B2\u09BE\u09A8\u09C7 ${formatCurrency(dueAmount, language)} \u09AC\u0995\u09C7\u09AF\u09BC\u09BE \u09A5\u09BE\u0995\u09AC\u09C7\u0964 \u0986\u09AA\u09A8\u09BF \u0995\u09BF \u09AC\u0995\u09C7\u09AF\u09BC\u09BE \u09A8\u09BF\u09AF\u09BC\u09C7 \u099A\u09BE\u09B2\u09BE\u09A8 \u09A4\u09C8\u09B0\u09BF \u0995\u09B0\u09A4\u09C7 \u099A\u09BE\u09A8?`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>
              {language === 'en' ? 'Cancel' : '\u09AC\u09BE\u09A4\u09BF\u09B2'}
            </AlertDialogCancel>
            <AlertDialogAction onClick={createInvoice} className="bg-orange-600 hover:bg-orange-700">
              {language === 'en' ? 'Create Anyway' : '\u09A4\u09AC\u09C1\u0993 \u09A4\u09C8\u09B0\u09BF \u0995\u09B0\u09C1\u09A8'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <PremiumUpgradeDialog
        open={upgradePopup.open}
        onOpenChange={(open) => setUpgradePopup(prev => ({ ...prev, open }))}
        feature={upgradePopup.feature}
        language={language}
      />
    </AdminLayout>
  );
}
